/**
 * Munja Trans GmbH – Transport-Plattform · Login
 *
 * © 2026 Aleksandar Ichkov. Alle Rechte vorbehalten.
 * Diese Anwendung wurde von Aleksandar Ichkov entwickelt und gehört ihm.
 * Built and owned by Aleksandar Ichkov.
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { Navigate, useLocation } from "react-router-dom";
import { Lock, AlertTriangle, Users, Truck, Calendar } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function useLoginStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/public/login-stats`).then(({ data }) => {
      if (!cancelled) setStats(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return stats;
}

function CountUp({ value, duration = 1200 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (typeof value !== "number") return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{n.toLocaleString("de-AT")}</>;
}

function StatTicker({ stats }) {
  // Three KPIs cycling every 4 s with fade transition
  const items = stats ? [
    { Icon: Users, value: stats.drivers_active, label: "aktive Fahrer", color: "#fbbf24" },
    { Icon: Truck, value: stats.vehicles_active, label: "Fahrzeuge im Einsatz", color: "#34d399" },
    { Icon: Calendar, value: stats.years_in_business, label: `Jahre seit ${stats.founded_year}`, color: "#60a5fa" },
  ] : [];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length === 0) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 4000);
    return () => clearInterval(id);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="mt-6 flex items-center gap-3 text-zinc-400 text-xs" data-testid="login-stat-ticker-loading">
        <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 animate-pulse" />
        <span>Lade Live-Zahlen…</span>
      </div>
    );
  }

  const it = items[idx];
  return (
    <div className="mt-6" data-testid="login-stat-ticker">
      <div
        key={idx}
        className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-zinc-950/55 backdrop-blur border border-white/10 shadow-lg animate-stat-in"
      >
        <it.Icon className="w-4 h-4" style={{ color: it.color }} />
        <div className="font-display font-bold text-2xl text-white leading-none tabular-nums">
          <CountUp value={it.value} />
        </div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-300">
          {it.label}
        </div>
      </div>
      <div className="flex gap-1.5 mt-3" aria-hidden>
        {items.map((_, i) => (
          <span
            key={i}
            className={`block h-0.5 rounded-full transition-all duration-500 ${
              i === idx ? "w-8 bg-amber-400" : "w-2 bg-white/20"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login, error } = useAuth();
  const tenantCtx = useTenant();
  const tenantBrand = tenantCtx?.tenant;
  const primaryColor = tenantBrand?.branding?.primary_color || "#C28535";
  const appName = tenantBrand?.name || "Munja Trans GmbH";
  const tenantSlug = tenantBrand?.slug || "";
  const stats = useLoginStats();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  if (user && user !== false) {
    // External accountants get routed to their dedicated portal
    if (user.external_accountant_id) {
      return <Navigate to="/steuerberater-portal" replace />;
    }
    const from = location.state?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  // Tenant slug provided in URL but no matching tenant — friendly message.
  if (tenantCtx?.mode === "tenant_unknown") {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="tenant-not-found-page">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-zinc-100 mb-2">Mandant nicht gefunden</h1>
          <p className="text-sm text-zinc-400">
            Die URL <span className="font-mono text-zinc-300" data-testid="tenant-not-found-slug">{tenantCtx.attempted}</span> ist aktuell keinem Kunden zugewiesen.
            Bitte prüfen Sie Ihre Login-URL oder kontaktieren Sie Ihren Administrator.
          </p>
        </div>
      </div>
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex" data-testid="login-page">
      {/* Hero panel (hidden on mobile) */}
      <div
        className="hidden lg:flex relative flex-1 overflow-hidden"
        data-testid="login-hero"
      >
        <img
          src="/munja-trucks-hero.jpg"
          alt="Munja Trans GmbH · LKW-Flotte"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/85 via-zinc-950/55 to-zinc-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
        {/* Hero content */}
        <div className="relative flex flex-col justify-between p-12 z-10 w-full">
          <div className="flex items-center gap-3">
            <img
              src="/munja-logo.png"
              alt="Munja Trans GmbH"
              className="w-12 h-12 rounded object-contain bg-zinc-900/70 p-1 ring-1 ring-white/10"
            />
            <div>
              <div className="font-display font-bold text-xl leading-none">Munja Trans GmbH</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/90 mt-1.5">
                Transport · seit 2011
              </div>
            </div>
          </div>
          <div className="max-w-lg">
            <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400/80 mb-3">
              Flotte · Dispo · Verwaltung
            </div>
            <h1 className="font-display text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Schnell.<br />Sicher.<br />Zuverlässig.
            </h1>
            <p className="mt-5 text-zinc-300 max-w-md text-sm leading-relaxed">
              Die interne Plattform für Disposition, Lenkprotokoll, Lohnabrechnung
              und Schwestergesellschaften — alles an einem Ort.
            </p>
            <StatTicker stats={stats} />
          </div>
          <div className="text-[10px] tracking-[0.18em] text-zinc-500 italic" data-testid="login-hero-credit">
            Entwicklung &amp; Eigentum: <span className="text-amber-400/90 not-italic font-semibold tracking-[0.12em]">Ichkov</span>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="w-full lg:w-[440px] xl:w-[480px] flex items-center justify-center p-6 lg:p-10 bg-zinc-950 relative z-10 lg:border-l lg:border-zinc-900">
        <div className="w-full max-w-sm">
          {/* Mobile brand (visible only when hero is hidden) */}
          <div className="lg:hidden mb-8 flex items-center gap-3 justify-center">
            <img
              src="/munja-logo.png"
              alt="Munja Trans GmbH"
              className="w-14 h-14 rounded object-contain bg-zinc-900 p-1"
              data-testid="login-tenant-logo"
            />
            <div>
              <div className="font-display font-bold text-xl text-zinc-50 leading-none" data-testid="login-app-name">
                {tenantSlug ? appName : "Munja Trans GmbH"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-500/80 mt-1.5">
                {tenantSlug ? `${tenantBrand?.plan || "Transport-Plattform"}` : "Transport-Plattform · seit 2011"}
              </div>
            </div>
          </div>

          <div className="mb-6 hidden lg:block">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-500/80 mb-2">Willkommen zurück</div>
            <h2 className="font-display text-2xl font-bold text-zinc-50">Sichere Anmeldung</h2>
            <p className="text-xs text-zinc-500 mt-1">Bitte melden Sie sich mit Ihren Zugangsdaten an.</p>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="login-form"
            className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-6 lg:p-7 backdrop-blur"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 mb-5 lg:hidden">
              <Lock className="w-3.5 h-3.5" />
              <span>Sichere Anmeldung{tenantSlug ? ` · ${tenantSlug}` : ""}</span>
            </div>

            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">E-Mail</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:border-transparent mb-4"
              style={{ "--tw-ring-color": primaryColor }}
              required
            />

            <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Passwort</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:border-transparent mb-6"
              style={{ "--tw-ring-color": primaryColor }}
              required
            />

            {error && (
              <div data-testid="login-error" className="mb-4 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">{error}</div>
            )}
            {!error && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("expired") === "1" && (
              <div data-testid="login-expired" className="mb-4 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-300">
                Ihre Sitzung ist abgelaufen. Bitte erneut anmelden.
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full disabled:opacity-50 text-white font-medium py-2.5 rounded-md transition-colors text-sm"
              style={{ background: primaryColor }}
            >
              {loading ? "Anmeldung..." : "Anmelden"}
            </button>
          </form>

          <div className="mt-5 text-[11px] text-zinc-600 text-center" data-testid="login-footer-slogan">
            {tenantSlug ? `${appName} · Transport-Plattform` : "Munja Trans GmbH · schnell · sicher · zuverlässig"}
          </div>
          <div className="mt-1.5 text-[10px] text-zinc-700 text-center italic tracking-[0.18em]" data-testid="login-developed-by">
            Entwicklung bei <span className="text-amber-500/80 not-italic font-semibold">Ichkov</span>
          </div>
        </div>
      </div>
    </div>
  );
}
